import { useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { updateDesktopShortcuts } from '../services/user/user.service';
import { fetchMenus, fetchDesktopShortcuts, setDesktopShortcutKeys } from '../stores/reducers/menu.reducer';

const MAX_SHORTCUTS = 6;

export default function useDesktopShortcuts() {
  const dispatch = useDispatch();
  const user = useSelector(state => state.auth.user);
  const { menus, menusLoaded, desktopShortcutKeys, desktopShortcutsLoaded } = useSelector(state => state.menu);

  useEffect(() => {
    if (!menusLoaded) {
      dispatch(fetchMenus());
    }
  }, [dispatch, menusLoaded]);

  useEffect(() => {
    if (!desktopShortcutsLoaded) {
      dispatch(fetchDesktopShortcuts());
    }
  }, [dispatch, desktopShortcutsLoaded]);

  const availableScreens = useMemo(() => {
    if (!menus.length) return [];
    const screens = [];
    [...menus]
      .sort((a, b) => a.sort_order - b.sort_order)
      .forEach(menu => {
        (menu.abilities || [])
          .filter(a => a.route)
          .filter(a => {
            if (user?.isAdmin) return true;
            return user?.abilities?.some(ua => ua.ability === a.ability);
          })
          .forEach(a => {
            screens.push({
              key: a.route,
              label: a.description,
              icon: a.icon || menu.icon,
              menuLabel: menu.label,
              path: `/${a.route}`,
            });
          });
      });
    return screens;
  }, [menus, user]);

  const shortcuts = useMemo(() => {
    return desktopShortcutKeys
      .map(key => availableScreens.find(s => s.key === key))
      .filter(Boolean);
  }, [desktopShortcutKeys, availableScreens]);

  const toggleShortcut = useCallback((key) => {
    const prev = desktopShortcutKeys;
    if (prev.includes(key)) {
      dispatch(setDesktopShortcutKeys(prev.filter(k => k !== key)));
    } else if (prev.length < MAX_SHORTCUTS) {
      dispatch(setDesktopShortcutKeys([...prev, key]));
    }
  }, [dispatch, desktopShortcutKeys]);

  const saveShortcuts = useCallback((keys) => {
    dispatch(setDesktopShortcutKeys(keys));
    updateDesktopShortcuts(keys).catch(() => {});
  }, [dispatch]);

  return {
    availableScreens,
    shortcuts,
    selectedKeys: desktopShortcutKeys,
    toggleShortcut,
    saveShortcuts,
    maxShortcuts: MAX_SHORTCUTS,
  };
}
